import { G, S } from '@mobily/ts-belt';
import cookies from 'browser-cookies';
import { clientAuthAxios } from '../clientAuth';
import { wafClientAuthAxios } from '../awsWaf';
import type { LoginAuthResponse } from './types';
export const ALLOWED_DOMAINS = ['https://helpcentertesting.stitchfix.com', 'https://support.stitchfix.com', 'https://www.stitchfix.com', 'https://stitchfix.com'] as const;
export const redirectUrlFromQueryParam = () => {
  const params = new URLSearchParams(window.location.search);

  // We typically want to redirect the user to the original url they were
  // attempting to view before being prompted to log in. However, in the
  // case of the ungated PDP, we need the ability to forward users to a
  // specific url via a `ret` param.
  const redirectParam = params.get('ret');

  // Catch if the sign-in is from Help Center chat widget
  // =====================================================================
  // A separate 'set'/'not set' 'fromchatwidget' flag param is used instead of
  // the 'ret' param due to the desired ToClientHome url having a different
  // subdomain. This will ensure there is only one way, and one place to
  // redirect someone away from https://stitchfix.com
  const fromChatWidget = params.get('fromchatwidget') !== null;
  if (fromChatWidget) {
    return 'https://support.stitchfix.com/hc/p/messaging-login';
  }
  if (G.isString(redirectParam) && S.isNotEmpty(redirectParam)) {
    let decodedRedirectParam: string;
    try {
      decodedRedirectParam = decodeURIComponent(redirectParam);
    } catch {
      return null;
    }

    // Absolute URL: allow only explicit trusted origins.
    try {
      const parsedUrl = new URL(decodedRedirectParam);
      if ((parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:') && ALLOWED_DOMAINS.includes(parsedUrl.origin as (typeof ALLOWED_DOMAINS)[number])) {
        return parsedUrl.toString();
      }
      return null;
    } catch {
      // Not an absolute URL; continue and treat as a relative path.
    }

    // Relative URL: normalize to a safe path on stitchfix.com.
    if (decodedRedirectParam.indexOf('/') !== 0) {
      decodedRedirectParam = `/${decodedRedirectParam}`;
    }
    return `https://www.stitchfix.com${decodedRedirectParam}`;
  }
  return null;
};
export const getRedirectUrl = () => redirectUrlFromQueryParam() || cookies.get('ret');

/**
 * Submits a request for passwordless login
 * If the request fails, the Promise will be rejected with an error containing the message
 */
export const requestPasswordlessLogin = (email: string) => {
  const redirectUrl = getRedirectUrl();
  return wafClientAuthAxios.post('passwordless_login', {
    email,
    target_platform: 'web',
    ...(redirectUrl && {
      redirect_url: redirectUrl
    })
  });
};
export const loginByEmail = (email: string, password: string, reCaptchaTokenV2?: string, reCaptchaTokenV3?: string, isWafEnabled = false) => {
  const apiService = isWafEnabled ? wafClientAuthAxios : clientAuthAxios;
  return apiService.post<LoginAuthResponse>('session', {
    user: {
      login: email,
      password,
      recaptchaToken: reCaptchaTokenV2,
      recaptchaTokenV3: reCaptchaTokenV3
    },
    scope: 'default'
  });
};
export const loginByAppleToken = (userId: string, authCode: string, isWafEnabled = false) => {
  const apiService = isWafEnabled ? wafClientAuthAxios : clientAuthAxios;
  return apiService.post<LoginAuthResponse>('linked_accounts/apple/login', {
    user_id: userId,
    auth_code: authCode,
    scope: 'default'
  });
};
export const loginByFacebookToken = (facebookToken: string, isWafEnabled = false) => {
  const apiService = isWafEnabled ? wafClientAuthAxios : clientAuthAxios;
  return apiService.post<LoginAuthResponse>('linked_accounts/facebook/login', {
    token: facebookToken,
    scope: 'default'
  });
};
export const loginByGoogleToken = (googleToken: string, isWafEnabled = false) => {
  const apiService = isWafEnabled ? wafClientAuthAxios : clientAuthAxios;
  return apiService.post<LoginAuthResponse>('linked_accounts/google/login', {
    token: googleToken,
    scope: 'default'
  });
};